import { useEffect, useState } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  {
    value: 'nom',
    label: 'Vouvoiement par nom',
    sample: 'Cher Lefresne',
    helper: 'Formule professionnelle classique. Recommandée par défaut.',
  },
  {
    value: 'prenom',
    label: 'Tutoiement par prénom',
    sample: 'Cher Yovan',
    helper: 'Plus chaleureux, garde le vouvoiement dans le corps du rapport.',
  },
  {
    value: 'neutre',
    label: 'Salutation neutre',
    sample: 'Bonjour',
    helper: 'Sans nom ni prénom, formule la plus sobre.',
  },
];

/**
 * Modale "Préférences" du rapport cabinet — réglage de la salutation
 * utilisée dans la lettre du cabinet et dans l'email de notification.
 */
export default function PreferencesModal({ open, onClose, onSaved }) {
  const [pref, setPref] = useState('nom');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('benchory_token');
    setLoading(true); setError('');
    fetch(`${API_URL}/client/profile/formality`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        setPref(data?.formality_preference || 'nom');
        setLoading(false);
      })
      .catch(() => { setError('Impossible de charger vos préférences.'); setLoading(false); });
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('benchory_token');
      const res = await fetch(`${API_URL}/client/profile/formality`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ formality_preference: pref }),
      });
      if (!res.ok) throw new Error('Échec de la sauvegarde');
      toast.success('Préférence de salutation enregistrée.');
      onSaved?.(pref);
      onClose();
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[1100]" data-testid="preferences-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="absolute inset-y-0 right-0 w-full max-w-md bg-cabinet-cream border-l border-cabinet-border shadow-2xl flex flex-col font-manrope text-cabinet-ink"
        data-testid="preferences-panel"
      >
        <div className="px-7 py-5 border-b border-cabinet-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] text-cabinet-bordeaux uppercase tracking-[0.25em] font-medium mb-1">
              Préférences
            </div>
            <h2 className="font-playfair text-[20px] text-cabinet-ink leading-tight">
              Comment souhaitez-vous être appelé ?
            </h2>
            <p className="text-[12px] text-cabinet-ink-secondary mt-2 leading-relaxed">
              Cette préférence est utilisée dans la salutation de la lettre du cabinet et dans l'email de notification mensuel.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="preferences-close"
            className="p-2 rounded-lg hover:bg-cabinet-cream-dark text-cabinet-ink-secondary"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-3">
          {loading ? (
            <div className="text-center py-8 text-[12px] text-cabinet-ink-soft">Chargement…</div>
          ) : (
            OPTIONS.map((opt) => {
              const isSelected = pref === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPref(opt.value)}
                  data-testid={`pref-option-${opt.value}`}
                  className={`w-full text-left p-4 border-2 transition ${
                    isSelected
                      ? 'border-cabinet-bordeaux bg-cabinet-paper'
                      : 'border-cabinet-border bg-cabinet-paper hover:border-cabinet-bordeaux/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.18em] font-medium mb-1">
                        {opt.label}
                      </div>
                      <div className="font-playfair italic text-[16px] text-cabinet-bordeaux">
                        {opt.sample}
                      </div>
                    </div>
                    <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center mt-1 ${
                      isSelected ? 'border-cabinet-bordeaux bg-cabinet-bordeaux' : 'border-cabinet-border'
                    }`}>
                      {isSelected && <Check size={10} className="text-cabinet-cream" strokeWidth={3} />}
                    </div>
                  </div>
                  <p className="text-[11px] text-cabinet-ink-secondary leading-relaxed">{opt.helper}</p>
                </button>
              );
            })
          )}

          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle size={12} /> {error}
            </div>
          )}
        </div>

        <div className="px-7 py-5 border-t border-cabinet-border bg-cabinet-cream-dark flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-cabinet-ink-secondary hover:text-cabinet-bordeaux"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="preferences-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-cabinet-bordeaux text-cabinet-cream text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-cabinet-bordeaux-dark disabled:opacity-50 transition"
          >
            <Check size={13} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}
